import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import ContactModeListComponent from "./ContactModeListComponent";
import ContactInfoSummary from "./ContactInfoSummary";
import ContactDataListComponent from "./ContactDataListComponent";
import { Grid, AccordionDetails, Accordion, AccordionSummary } from "@mui/material";
import { CollapseCard, Typography } from "@totalsoft/rocket-ui";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import useLocator from "hooks/useLocator";
import { useReactiveVar } from "@apollo/client";
import { synchronizationInProgressVar } from "variables";

export const ContactInfoComponent = ({
  contactModeList,
  contactInfoList,
  contactModes,
  contactModesValidation,
  contactInfosValidation,
  positions,
  addContactMode,
  addContactInfo,
  saveContactInfos,
  deleteContactInfo,
  saveContactModes,
  deleteContactMode,
  onContactModePropertyChange,
  onContactInfoPropertyChange,
  loadPersons,
  partnerId,
  onContactModeCardExpand,
  contactModeCardExpanded,
  onContactInfoCardExpand,
  contactInfoCardExpanded,
  saveContactModeDisabled,
  saveContactInfoDisabled,
  validDefaultContactInfo,
  validESignatureContactInfo,
  validDefaultEmail,
  validDefaultPhone,
  validDefaultMobilePhone,
  loadPartnerDetails,
  partnerContactInfoSummary,
  hasWritePermission
}) => {
  const { t } = useTranslation();
  const synchronizationInProgress = useReactiveVar(synchronizationInProgressVar);
  const { isSelfPartner } = useLocator();

  const [expanded, setExpanded] = useState(false);

  const hideContactPersons = useMemo(() => isSelfPartner, [isSelfPartner]);

  return (
    <CollapseCard
      id="contactInfoCard"
      icon={ContactMailIcon}
      iconColor="primary"
      subheader={<ContactInfoSummary contactInfoSummary={partnerContactInfoSummary} />}
      title={t("Partner.ContactInfo")}
      onToggle={(_, expanded) => {
        setExpanded(!expanded);
        onContactModeCardExpand(!expanded);
        onContactInfoCardExpand(!expanded);
      }}
      expanded={expanded}
      defaultExpanded={synchronizationInProgress}
      hideSubheaderOnExpand
      toggleOnHeaderClick
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Accordion
            id="contactModeCard"
            defaultExpanded
            expanded={contactModeCardExpanded}
            onChange={(_, expanded) => onContactModeCardExpand(expanded)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" color={"initial"}>
                {t("Partner.PhoneAndEmail")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ContactModeListComponent
                contactModeList={contactModeList}
                contactModes={contactModes}
                validation={contactModesValidation}
                addContactMode={addContactMode}
                deleteContactMode={deleteContactMode}
                onPropertyChange={onContactModePropertyChange}
                saveContactModes={saveContactModes}
                partnerId={partnerId}
                saveDisabled={saveContactModeDisabled}
                validDefaultEmail={validDefaultEmail}
                validDefaultMobilePhone={validDefaultMobilePhone}
                validDefaultPhone={validDefaultPhone}
                hasWritePermission={hasWritePermission}
              />
            </AccordionDetails>
          </Accordion>
        </Grid>
        {!hideContactPersons && (
          <Grid item xs={12}>
            <Accordion
              id="contactDataCard"
              defaultExpanded
              expanded={contactInfoCardExpanded}
              onChange={(_, expanded) => onContactInfoCardExpand(expanded)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" color={"initial"}>
                  {t("Partner.ContactPerson")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ContactDataListComponent
                  contactInfoList={contactInfoList}
                  validation={contactInfosValidation}
                  positions={positions}
                  addContactInfo={addContactInfo}
                  onPropertyChange={onContactInfoPropertyChange}
                  saveContactInfos={saveContactInfos}
                  deleteContactInfo={deleteContactInfo}
                  loadPersons={loadPersons}
                  partnerId={partnerId}
                  saveDisabled={saveContactInfoDisabled}
                  validDefaultContactInfo={validDefaultContactInfo}
                  validESignatureContactInfo={validESignatureContactInfo}
                  loadPartnerDetails={loadPartnerDetails}
                  hasWritePermission={hasWritePermission}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}
      </Grid>
    </CollapseCard>
  );
};

ContactInfoComponent.propTypes = {
  contactModeList: PropTypes.array,
  contactInfoList: PropTypes.array,
  contactModes: PropTypes.array.isRequired,
  contactModesValidation: PropTypes.object.isRequired,
  contactInfosValidation: PropTypes.object.isRequired,
  positions: PropTypes.array.isRequired,
  onContactInfoPropertyChange: PropTypes.func.isRequired,
  onContactModePropertyChange: PropTypes.func.isRequired,
  loadPersons: PropTypes.func.isRequired,
  partnerId: PropTypes.number,
  onContactModeCardExpand: PropTypes.func.isRequired,
  contactModeCardExpanded: PropTypes.bool,
  onContactInfoCardExpand: PropTypes.func.isRequired,
  contactInfoCardExpanded: PropTypes.bool,
  addContactMode: PropTypes.func.isRequired,
  deleteContactMode: PropTypes.func.isRequired,
  saveContactModes: PropTypes.func.isRequired,
  addContactInfo: PropTypes.func.isRequired,
  deleteContactInfo: PropTypes.func.isRequired,
  saveContactInfos: PropTypes.func.isRequired,
  saveContactModeDisabled: PropTypes.bool,
  saveContactInfoDisabled: PropTypes.bool,
  validDefaultContactInfo: PropTypes.bool,
  validESignatureContactInfo: PropTypes.bool,
  validDefaultEmail: PropTypes.bool,
  validDefaultPhone: PropTypes.bool,
  validDefaultMobilePhone: PropTypes.bool,
  loadPartnerDetails: PropTypes.func,
  partnerContactInfoSummary: PropTypes.object,
  hasWritePermission: PropTypes.bool.isRequired
};

export default ContactInfoComponent;
